import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Messages = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const userId = searchParams.get('user');
    if (userId && conversations.length > 0) {
      const conv = conversations.find((c) => c.user_id === userId);
      if (conv) {
        selectConversation(conv);
      }
    }
  }, [searchParams, conversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.user_id);
      const interval = setInterval(() => fetchMessages(selectedConversation.user_id), 3000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const response = await axios.get(`${API}/messages/${otherUserId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.user_id);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedConversation) return;

    try {
      await axios.post(`${API}/messages`, {
        recipient_id: selectedConversation.user_id,
        content: messageInput,
      });
      setMessageInput('');
      fetchMessages(selectedConversation.user_id);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  return (
    <div data-testid="messages-content">
      <h1 className="text-3xl font-semibold mb-6">Messages</h1>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" style={{ height: '600px' }}>
        <div className="grid grid-cols-3 h-full">
          <div className="col-span-1 border-r">
            <ScrollArea className="h-full">
              {loading ? (
                <div className="p-4 text-center text-gray-600">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-600">No conversations yet</div>
              ) : (
                <div className="divide-y">
                  {conversations.map((conv) => (
                    <button
                      key={conv.user_id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                        selectedConversation?.user_id === conv.user_id ? 'bg-purple-50' : ''
                      }`}
                      data-testid={`conversation-${conv.user_id}`}
                    >
                      <div className="font-semibold mb-1">{conv.user_name}</div>
                      <div className="text-sm text-gray-600 truncate">{conv.last_message}</div>
                      {conv.unread_count > 0 && (
                        <div className="mt-2">
                          <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                            {conv.unread_count}
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <div className="col-span-2 flex flex-col">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b bg-gray-50">
                  <div className="font-semibold">{selectedConversation.user_name}</div>
                </div>

                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                        data-testid={`message-${msg.id}`}
                      >
                        <div
                          className={`chat-bubble px-4 py-2 rounded-2xl ${
                            msg.sender_id === user.id
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                <form onSubmit={sendMessage} className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="send-message-button">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600">
                Select a conversation to start messaging
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
